'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers'

const SigmaBars = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.SigmaBars), { ssr: false })
const BalanceMean = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.BalanceMean), { ssr: false })

function Decoder() {
  const parts = [
    { tex: '\\sum', say: '“add up”', color: 'var(--gd-accent)' },
    { tex: 'i = 1', say: 'start the counter i at 1', color: 'var(--gd-blue)' },
    { tex: 'n', say: 'stop when i reaches n', color: 'var(--gd-blue)' },
    { tex: 'x_i', say: 'the thing to add each time', color: 'var(--gd-amber)' },
  ]
  return (
    <div className="grid gap-5 py-2 sm:grid-cols-[auto_1fr] sm:items-center">
      <div className="text-center text-[34px]">
        <M>{'\\accent{\\sum}_{\\blue{i=1}}^{\\blue{n}} \\amber{x_i}'}</M>
      </div>
      <ul className="m-0 grid list-none gap-2 p-0">
        {parts.map(p => (
          <li key={p.tex} className="flex items-baseline gap-3 text-[15.5px]">
            <span className="min-w-[56px] text-right" style={{ color: p.color }}>
              <M>{p.tex}</M>
            </span>
            <span style={{ color: 'var(--gd-ink-2)' }}>{p.say}</span>
          </li>
        ))}
        <li className="gd-mono mt-1 text-[13px]" style={{ color: 'var(--gd-ink-3)' }}>
          {'≡ for (let i = 1; i <= n; i++) total += x[i]'}
        </li>
      </ul>
    </div>
  )
}

export default function SumsAndAverages() {
  return (
    <Lesson>
      <Step>
        <H>Σ is a for-loop</H>
        <P>
          This symbol scares more people away from machine learning than anything else: <M>{'\\sum'}</M>. It’s the Greek capital
          letter sigma (an “S”, for <em>sum</em>), and it means exactly one thing: <strong>add up a list of terms</strong>. You
          already know how — it’s the loop you wrote in Module 1.
        </P>
        <Figure n="2.15" caption="Each term becomes a bar; the bars add up to the total. Change the term and the stopping point n.">
          <SigmaBars />
        </Figure>
      </Step>

      <Step gate="nq-sigma">
        <H>Reading the notation</H>
        <P>Every sum has the same four parts. Once you can read them, the scariest-looking formula becomes a loop:</P>
        <Figure n="2.16" caption="Anatomy of a sum.">
          <Decoder />
        </Figure>
        <Exercise id="sigma-loop" title="Σ as code" {...EX.sigmaLoop} />
        <NumberQuiz id="nq-sigma" question={<>What is <M>{'\\sum_{i=1}^{3} 2i'}</M>?</>} answer={12} explain={<>2·1 + 2·2 + 2·3 = 2 + 4 + 6 = 12.</>} hint={<>Plug in i = 1, 2 and 3 into 2i, then add the three results.</>} />
      </Step>

      <Step gate="q-mean">
        <H>The mean is a balance point</H>
        <P>
          The <strong>mean</strong> (or average) of a list is its sum divided by how many items there are. In sigma notation:
        </P>
        <MB>{'\\bar{x} = \\frac{1}{n} \\sum_{i=1}^{n} x_i'}</MB>
        <Callout kind="notation">
          The bar over <M>{'\\bar{x}'}</M> (say “x-bar”) is the traditional symbol for a mean. The <M>{'\\tfrac{1}{n}'}</M> out
          front just means “then divide by n”.
        </Callout>
        <P>
          There’s a lovely way to <em>see</em> it: put each value as a weight on a plank. The mean is exactly where you’d have to
          put the pivot to make it balance.
        </P>
        <Figure n="2.17" caption="Drag the weights. The pivot always sits at the mean — far-away weights pull it harder.">
          <BalanceMean />
        </Figure>
        <Quiz
          id="q-mean"
          question="You have the values 1, 2, 3 and 100. Where does the balance point (mean) sit?"
          options={[
            { label: 'Around 2.5 — the middle of the list', why: 'The middle position is the median. The mean feels the 100’s weight.' },
            { label: '26.5', correct: true },
            { label: '100', why: 'The 100 pulls hard, but three small weights pull back.' },
          ]}
          explain="(1 + 2 + 3 + 100) ÷ 4 = 26.5. One extreme value drags the mean a long way — remember this when a single bad data point throws off a model."
        />
      </Step>

      <Step gate="sigma-mean">
        <H>Build them</H>
        <Exercise
          id="sigma-mean"
          title="sigma and mean"
          {...EX.sigmaMean}
          hints={[
            <>
              Loop <C>{'for (let i = 1; i <= n; i++)'}</C> and add <C>f(i)</C> each time.
            </>,
            <>Mean: add everything up, then divide by <C>xs.length</C>.</>,
          ]}
        />
      </Step>

      <Step gate="mse">
        <H>The formula you can now read</H>
        <P>Here is the most famous formula in machine learning. Ten lessons ago it would have been gibberish:</P>
        <MB>{'\\pink{\\text{MSE}} = \\frac{1}{n} \\sum_{i=1}^{n} \\left(\\amber{y_i} - \\amber{\\hat{y}_i}\\right)^2'}</MB>
        <P>
          Read it right to left: for each example <em>i</em>, take the true value minus the prediction (the error), square it
          (so it’s positive, and big misses hurt more), add them all up, divide by how many there are. The{' '}
          <strong>mean squared error</strong>: one number that says how wrong a model is on average.
        </P>
        <Exercise
          id="mse"
          title="Mean squared error"
          {...EX.mse}
          hints={[
            <>Loop over the indices. Compute the error, square it, add it to a total.</>,
            <>
              Don’t forget to divide by <C>actual.length</C> at the end.
            </>,
          ]}
        />
        <Callout kind="why">
          You’ve now built the core vocabulary: numbers, functions, lines, exponentials, logs, sums and averages. Next we level
          up from single numbers to <strong>lists of numbers treated as one thing</strong> — vectors.
        </Callout>
      </Step>
    </Lesson>
  )
}
